import { AddIcon, CloseIcon } from '@systemeio/ui/icons'
import { CurrencyEnum, FormSelect, PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest, InternalError } from '@systemeio/util'
import { DeleteConfirmationLoader } from 'modules/digital-product/components/delete-confirmation-loader'
import PricePlanDescription from 'modules/digital-product/price-plans/components/price-plan-description'
import { usePricePlansApi } from 'modules/digital-product/price-plans/hooks/use-price-plans-api'
import { usePricePlansCount } from 'modules/digital-product/price-plans/hooks/use-price-plans-count'
import { useUserPricePlans } from 'modules/digital-product/price-plans/hooks/use-user-price-plans'
import {
  getAvailablePricePlansData,
  getPricePlansTypesData,
} from 'modules/digital-product/price-plans/utils/price-plans-utils'
import React, { useEffect, useRef, useState } from 'react'
import ButtonLikeLink from 'shared/components/button-like-link'
import Confirmation from 'shared/components/confirmation-modal'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { PricePlanType, PricePlanTypeEnum } from '@systemeio/types'

interface AddPricePlanProps {
  productPricePlans: PricePlanType[]
  onAddPricePlan: (value: PricePlanType) => Promise<void>
  openCreateForm: () => void
  closeForm: () => void
  currency: CurrencyEnum
}

function AddPricePlan({
  productPricePlans,
  onAddPricePlan,
  openCreateForm,
  closeForm,
  currency,
}: AddPricePlanProps) {
  const { t } = useLocoTranslation()
  const [pricePlanId, setPricePlanId] = useState<PricePlanType['id']>()
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const [type, setType] = useState<PricePlanTypeEnum>(PricePlanTypeEnum.one_shot)

  const [pricePlanToDelete, setPricePlanToDelete] = useState<null | PricePlanType>(null)
  const [opened, setOpened] = useState(false)

  const { data: countData } = usePricePlansCount(opened, pricePlanToDelete?.id)

  const { userPricePlans, mutateUserPricePlans } = useUserPricePlans(currency)

  const [availablePricePlansData, setAvailablePricePlansData] = useState(
    getAvailablePricePlansData({
      pricePlansList: userPricePlans,
      existingPricePlans: productPricePlans,
      type,
      inputRef,
      onClickBin: pricePlan => {
        setPricePlanToDelete(pricePlan)
        setOpened(true)
      },
    }),
  )

  useEffect(() => {
    if (userPricePlans) {
      setAvailablePricePlansData(
        getAvailablePricePlansData({
          pricePlansList: userPricePlans,
          existingPricePlans: productPricePlans,
          type,
          inputRef,
          onClickBin: pricePlan => {
            setPricePlanToDelete(pricePlan)
            setOpened(true)
          },
        }),
      )
    }
  }, [userPricePlans, type, productPricePlans])

  const handleConfirm = async () => {
    try {
      const pricePlan = userPricePlans?.find(el => el.id === pricePlanId)
      if (!pricePlan) return
      setError('')
      setIsFetching(true)
      await onAddPricePlan(pricePlan)
      setIsFetching(false)
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''))
        }
      } else if (e instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  const selectedPricePlan = userPricePlans?.find(el => el.id === pricePlanId)

  const { deletePricePlan } = usePricePlansApi()

  const handleRemovePricePlan = async () => {
    if (pricePlanToDelete) {
      try {
        await deletePricePlan(pricePlanToDelete.id)
        await mutateUserPricePlans(data => {
          if (data) {
            return data.filter(pricePlan => pricePlan.id !== pricePlanToDelete.id)
          }
        }, false)
        setPricePlanId(undefined)
        setPricePlanToDelete(null)
      } catch (error) {}
    }
  }

  return (
    <>
      <div className="border-text-secondary/30 relative flex flex-col gap-5 rounded-lg border px-5 py-3">
        <CloseIcon
          className={`cursor-pointer ${
            isFetching && 'fill-text-secondary pointer-events-none'
          } absolute right-1 top-1 lg:right-2 lg:top-2`}
          onClick={() => closeForm()}
        />
        <FormSelect
          label={t('dashboard.funnel.configuration.common.price_plan.type_label')}
          data={getPricePlansTypesData(t)}
          withoutCloseIcon
          onChange={type => {
            if (!type) return
            setType(type)
            setPricePlanId(undefined)
          }}
          value={type}
        />
        <FormSelect
          inputRef={inputRef}
          label={t('dashboard.funnel.configuration.common.price_plan.choose_existing')}
          data={availablePricePlansData}
          onChange={setPricePlanId}
          value={pricePlanId}
        />
        {selectedPricePlan && <PricePlanDescription pricePlan={selectedPricePlan} />}
        <div
          className={
            'text-text-primary flex w-full flex-col gap-2 text-sm font-medium lg:flex-row lg:text-[15px]'
          }
        >
          <span>{t('dashboard.funnel.configuration.offer.add_price_plan_footer')}</span>
          <ButtonLikeLink
            onClick={() => {
              openCreateForm()
            }}
          >
            {t('dashboard.funnel.configuration.offer.add_price_plan_footer_link')}
          </ButtonLikeLink>
        </div>
        <div className="flex">
          <PrimaryButton
            disabled={!pricePlanId}
            width="large"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              handleConfirm()
            }}
            isFetching={isFetching}
            type="submit"
          >
            <AddIcon />
            {t('global.save')}
          </PrimaryButton>
        </div>
        <FieldErrorAndDescription error={error} errorClassName={'text-center mt-2'} />
      </div>
      <Confirmation
        opened={opened}
        onClose={() => setOpened(false)}
        onConfirm={handleRemovePricePlan}
        confirmationContent={
          <>
            {countData ? (
              <div className="text-text-primary text-base">
                {t('dashboard.offer.digital_product.price_plans.remove_description_with_count', {
                  pricePlanName: pricePlanToDelete?.innerName,
                  numberOfSteps: countData.count,
                })}
              </div>
            ) : (
              <DeleteConfirmationLoader />
            )}
          </>
        }
      />
    </>
  )
}

export default AddPricePlan
